import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';

import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { removeGlobalLimitedCostGql, removeGlobalOtherCostGql } from '../catalogOtherAndLimitedQueries';
import { CatalogOtherAndLimitedTab } from '../types';

export const useRemoveCatalogCost = ({ tab, onSuccess }: { tab: CatalogOtherAndLimitedTab; onSuccess: () => void }) => {
    const dispatch = useDispatch();
    const [removingId, setRemovingId] = useState<number | null>(null);
    const isOthersTab = tab === CatalogOtherAndLimitedTab.Others;

    const [resetRemoveOther] = useResetStoreData({ requestId: 'removeGlobalOtherCost' });
    const [resetRemoveLimited] = useResetStoreData({ requestId: 'removeGlobalLimitedCost' });

    const onRemoveOtherSuccess = useCallback(
        ({ payload }: { payload?: { removeGlobalOtherCost?: { result?: boolean } } }) => {
            if (payload?.removeGlobalOtherCost?.result) {
                onSuccess();
                setRemovingId(null);
                resetRemoveOther(undefined);
            }
        },
        [onSuccess, resetRemoveOther],
    );
    const onRemoveLimitedSuccess = useCallback(
        ({ payload }: { payload?: { removeGlobalLimitedCost?: { result?: boolean } } }) => {
            if (payload?.removeGlobalLimitedCost?.result) {
                onSuccess();
                setRemovingId(null);
                resetRemoveLimited(undefined);
            }
        },
        [onSuccess, resetRemoveLimited],
    );

    const [, , , removeOtherRequest] = useRequest({
        requestId: 'removeGlobalOtherCost',
        query: removeGlobalOtherCostGql,
        withEffect: false,
        dependenciesUpdater: onRemoveOtherSuccess,
    });
    const [, , , removeLimitedRequest] = useRequest({
        requestId: 'removeGlobalLimitedCost',
        query: removeGlobalLimitedCostGql,
        withEffect: false,
        dependenciesUpdater: onRemoveLimitedSuccess,
    });

    const handleRemove = useCallback(() => {
        if (removingId === null) {
            return;
        }

        if (isOthersTab) {
            dispatch(removeOtherRequest({ id: removingId }));

            return;
        }

        dispatch(removeLimitedRequest({ id: removingId }));
    }, [dispatch, isOthersTab, removeLimitedRequest, removeOtherRequest, removingId]);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: handleRemove,
        });

    const handleOpenRemoveDialog = useCallback(
        (removeId: number) => () => {
            setRemovingId(removeId);
            handleOpenConfirmationDialog();
        },
        [handleOpenConfirmationDialog],
    );

    return useMemo(
        () => ({
            isOpenConfirmationDialog,
            handleOpenRemoveDialog,
            handleCloseConfirmationDialog,
            handleActionQuery,
        }),
        [handleActionQuery, handleCloseConfirmationDialog, handleOpenRemoveDialog, isOpenConfirmationDialog],
    );
};
